import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect } from 'react'
import { CalendarDays, X } from 'lucide-react'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

/**
 * Pregunta la fecha de vencimiento antes de subir un documento del expediente.
 *
 * La captura el proveedor a mano: no se deriva de la vigencia del tipo de
 * documento ni de nada automático (decisión confirmada con Karen, C-05). Y va
 * junto con la subida porque el proveedor no tiene endpoint para corregirla
 * después: si se sube sin fecha, solo Contraloría puede ponerla al revisar.
 *
 * Es opcional: hay documentos que no vencen.
 */
/**
 * Ultimo dia del mes en curso, en YYYY-MM-DD y en hora local.
 *
 * El dia 0 del mes siguiente es el ultimo del actual. Se arma con los
 * componentes y no con toISOString(), que convierte a UTC y en Mexico devuelve
 * el dia anterior.
 */
const ultimoDiaDelMes = (hoy = new Date()) => {
  const ultimo = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)
  const mes = String(ultimo.getMonth() + 1).padStart(2, '0')
  const dia = String(ultimo.getDate()).padStart(2, '0')
  return `${ultimo.getFullYear()}-${mes}-${dia}`
}

/**
 * Documentos cuya vigencia se sugiere sola. Hoy solo la Opinion de Cumplimiento
 * SAT, que es del mes en curso (C-05): se propone el ultimo dia del mes y el
 * proveedor la puede cambiar.
 */
const SUGIERE_FIN_DE_MES = ['EXP_OPINION_SAT']

interface FechaVencimientoModalProps {
  abierto: boolean
  nombreDocumento: string
  /** Codigo del tipo de documento, para saber si se sugiere una fecha. */
  codigoDocumento?: string
  /** Fecha que ya tenía el documento, al reemplazarlo. Formato YYYY-MM-DD. */
  fechaInicial?: string
  subiendo?: boolean
  onCancelar: () => void
  onConfirmar: (validUntil: string | undefined) => void
}

const FechaVencimientoModal = ({
  abierto,
  nombreDocumento,
  codigoDocumento,
  fechaInicial = '',
  subiendo = false,
  onCancelar,
  onConfirmar,
}: FechaVencimientoModalProps) => {
  const [fecha, setFecha] = useState(fechaInicial)

  // Se reinicia en cada apertura: si no, el segundo documento hereda la fecha
  // que se capturó para el primero.
  useEffect(() => {
    if (!abierto) return
    // La sugerencia no pisa lo que ya tenia el documento al reemplazarlo.
    const sugerida =
      !fechaInicial && codigoDocumento && SUGIERE_FIN_DE_MES.includes(codigoDocumento)
        ? ultimoDiaDelMes()
        : fechaInicial
    setFecha(sugerida)
  }, [abierto, fechaInicial, codigoDocumento])

  if (!abierto) return null

  return (
    <CapaDeModal zIndex={70}>
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <button
          onClick={onCancelar}
          disabled={subiendo}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 pr-6">
          <CalendarDays className="w-5 h-5 text-[#315FB9]" />
          <h2 className="text-lg font-bold text-gray-900">Fecha de vencimiento</h2>
        </div>
        <p className="text-sm text-gray-500 mt-1">{nombreDocumento}</p>

        <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
          ¿Hasta cuándo es vigente este documento?
        </label>
        <input
          type="date"
          value={fecha}
          disabled={subiendo}
          onChange={(e) => setFecha(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
        />
        <p className="text-xs text-gray-500 mt-1">
          {codigoDocumento && SUGIERE_FIN_DE_MES.includes(codigoDocumento)
            ? 'Se propone el último día del mes en curso porque esta opinión es mensual. Puedes cambiarla.'
            : 'Si el documento no vence, déjalo vacío. Contraloría puede corregir la fecha al revisarlo.'}
        </p>

        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={onCancelar}
            disabled={subiendo}
            className={estiloDeBoton('secundario')}
          >
            Cancelar
          </button>
          <button
            onClick={() => onConfirmar(fecha || undefined)}
            disabled={subiendo}
            className={estiloDeBoton('primario')}
          >
            {subiendo ? 'Subiendo…' : 'Subir documento'}
          </button>
        </div>
      </div>
    </CapaDeModal>
  )
}

export default FechaVencimientoModal
